import { apiClient } from './api';
import type {
  BankServiceRun,
  BankServiceRunListResult,
  CreateBankServiceRunRequest,
  SirketDto,
  SirketHesapDto,
  SirketListPagedResult,
} from '../types/bankServiceRun';

export const bankServiceRunService = {
  getList: async (params: {
    serverId?: string;
    tenantId?: string;
    status?: number;
    page?: number;
    pageSize?: number;
  }): Promise<BankServiceRunListResult> => {
    const queryParams = new URLSearchParams();
    if (params.serverId) queryParams.append('serverId', params.serverId);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.status !== undefined) queryParams.append('status', params.status.toString());
    queryParams.append('page', (params.page ?? 1).toString());
    queryParams.append('pageSize', (params.pageSize ?? 20).toString());

    const response = await apiClient.get<BankServiceRunListResult>(
      `/EStatement/bank-service-run?${queryParams.toString()}`
    );
    return response.data;
  },

  getById: async (id: string): Promise<BankServiceRun> => {
    const response = await apiClient.get<BankServiceRun>(`/EStatement/bank-service-run/${id}`);
    return response.data;
  },

  create: async (data: CreateBankServiceRunRequest): Promise<string> => {
    const response = await apiClient.post<string>('/EStatement/bank-service-run', data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/EStatement/bank-service-run/${id}`);
  },

  retry: async (
    id: string,
    requestParameters?: string,
    requestResponseDownload?: boolean
  ): Promise<void> => {
    await apiClient.post(`/EStatement/bank-service-run/${id}/retry`, {
      requestParameters: requestParameters ?? undefined,
      requestResponseDownload: requestResponseDownload ?? undefined,
    });
  },

  downloadRequestResponse: async (id: string): Promise<void> => {
    const response = await apiClient.get<Blob>(
      `/EStatement/bank-service-run/${id}/download-request-response`,
      { responseType: 'blob' }
    );
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `request-response_${id}.txt`;
    if (typeof contentDisposition === 'string') {
      const match = /filename="?([^";\n]+)"?/.exec(contentDisposition);
      if (match?.[1]) fileName = match[1].trim();
    }
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
  },

  getSirketler: async (serverId: string): Promise<SirketDto[]> => {
    const response = await apiClient.get<SirketDto[]>('/EStatement/sirketler', {
      params: { serverId },
    });
    return response.data;
  },

  getSirketlerPaged: async (params: {
    serverId?: string;
    tenantId?: string;
    search?: string;
    vkn?: string;
    unvan?: string;
    page: number;
    pageSize: number;
  }): Promise<SirketListPagedResult> => {
    const queryParams = new URLSearchParams();
    if (params.serverId) queryParams.append('serverId', params.serverId);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.search) queryParams.append('search', params.search);
    if (params.vkn) queryParams.append('vkn', params.vkn);
    if (params.unvan) queryParams.append('unvan', params.unvan);
    queryParams.append('page', params.page.toString());
    queryParams.append('pageSize', params.pageSize.toString());
    const response = await apiClient.get<SirketListPagedResult>(
      `/EStatement/sirketler/paged?${queryParams.toString()}`
    );
    return response.data;
  },

  getSirketHesaplar: async (
    serverId: string,
    vkn?: string,
    bankaKodu?: number
  ): Promise<SirketHesapDto[]> => {
    const params: Record<string, string> = { serverId };
    if (vkn) params.vkn = vkn;
    if (bankaKodu !== undefined) params.bankaKodu = bankaKodu.toString();
    const response = await apiClient.get<SirketHesapDto[]>('/EStatement/sirket-hesaplar', {
      params,
    });
    return response.data;
  },
};
